import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { logErrorToDatadog, logConversationEvent } from '@/common/utils'
import { createDirectLine, createStore } from 'botframework-webchat'
import { currentActionMiddleware } from '../../react-components/middleware/current-action'
import { userActivityHistoryMiddleware } from '../../react-components/middleware/user-activity-history'
import { conversationEventsMiddleware } from '../../react-components/middleware/conversation'
import {
  ActionHistory,
  clearWebchatRuntime,
  getWebchatRuntime,
  setUserActivityHistoryEntry,
  setWebchatBotConnection,
  setWebchatBotStore,
} from '../webchat-runtime'
import type { ThunkDispatch } from '../thunk'
import type { AuthRootState } from './user'
import { overlayReset } from './bot-events'
import { resetForNewSession } from './bot-view'

export interface UserObj {
  id: string
  applicationId: string
  customerId: string
}

export type LocalDispatch = (type: string, payload?: unknown) => unknown

export interface RegisterWebchatPayload {
  directLine: {
    conversationId: string
    token: string
    user: string
  }
  api: {
    token: string
  }
  user: UserObj
}

interface WebchatState {
  conversation: string | undefined
  token: string | undefined
  userId: string | undefined
  userObj: UserObj | undefined
  currentAction: ActionHistory | undefined
  enableEditAction: boolean
}

type WindowWithVsa = Window & {
  vsa?: {
    registerAgent: (payload: string) => void
  }
}

type DirectLineClient = ReturnType<typeof createDirectLine>

const initialState: WebchatState = {
  conversation: undefined,
  token: undefined,
  userId: undefined,
  userObj: undefined,
  currentAction: undefined,
  enableEditAction: false,
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isActionHistory = (value: unknown): value is ActionHistory => {
  if (!isRecord(value)) {
    return false
  }
  return typeof value.actionId === 'string' && typeof value.isEditableAction === 'boolean'
}

const webchatSlice = createSlice({
  name: 'webchat',
  initialState,
  reducers: {
    conversation(state, action: PayloadAction<string | undefined>) {
      const previousConversationId = state.conversation
      state.conversation = action.payload

      if (action.payload && (!previousConversationId || action.payload !== previousConversationId)) {
        const eventType = previousConversationId
          ? 'conversation_changed'
          : 'conversation_assigned'
        logConversationEvent(eventType, action.payload, state.userObj)
      }
    },
    token(state, action: PayloadAction<string | undefined>) {
      state.token = action.payload
    },
    userId(state, action: PayloadAction<string | undefined>) {
      state.userId = action.payload
    },
    userObj(state, action: PayloadAction<UserObj | undefined>) {
      state.userObj = action.payload
    },
    CURRENT_ACTION(state, action: PayloadAction<ActionHistory | undefined>) {
      state.currentAction = action.payload
    },
    CAN_EDIT_ACTIONS(state, action: PayloadAction<boolean>) {
      state.enableEditAction = action.payload
    },
  },
})

export const {
  conversation,
  token,
  userId,
  userObj,
  CURRENT_ACTION,
  CAN_EDIT_ACTIONS,
} = webchatSlice.actions

const getBotFrameworkStore = () => getWebchatRuntime().store

export const UPDATE_CURRENT_ACTION =
  (payload: unknown) => (dispatch: ThunkDispatch) => {
    if (isActionHistory(payload)) {
      dispatch(CURRENT_ACTION(payload))
      dispatch(CAN_EDIT_ACTIONS(payload.isEditableAction))
      return
    }
    dispatch(CURRENT_ACTION(undefined))
    dispatch(CAN_EDIT_ACTIONS(false))
  }

export const DISABLE_EDIT_ACTIONS = () => (dispatch: ThunkDispatch) => {
  dispatch(CAN_EDIT_ACTIONS(false))
}

export const SET_JOURNEY_COMPLETE = () => (dispatch: ThunkDispatch) => {
  dispatch(CURRENT_ACTION(undefined))
  dispatch(CAN_EDIT_ACTIONS(false))
}

export const UPDATE_USER_ACTIVITY_HISTORY =
  (activityId: string) => (_dispatch: ThunkDispatch, getState: () => AuthRootState) => {
    setUserActivityHistoryEntry(activityId, getState().webchat.currentAction)
  }

export const sendEvent = (payload: unknown) => () => {
  getBotFrameworkStore()?.dispatch({
    type: 'WEB_CHAT/SEND_EVENT',
    payload,
  })
}

export const sendMessage = (payload: { message: string; [key: string]: unknown }) => () => {
  getBotFrameworkStore()?.dispatch({
    type: 'WEB_CHAT/SEND_MESSAGE',
    payload: { text: payload.message, channelData: { data: payload } },
  })
}

export const postActivity = (activity: unknown) => () => {
  getBotFrameworkStore()?.dispatch({
    type: 'DIRECT_LINE/POST_ACTIVITY',
    meta: { method: 'keyboard' },
    payload: { activity },
  })
}

export const createWebchatLocalDispatch =
  (dispatch: ThunkDispatch): LocalDispatch =>
  (type, payload) => {
    switch (type) {
      case 'UPDATE_CURRENT_ACTION':
        return dispatch(UPDATE_CURRENT_ACTION(payload))
      case 'DISABLE_EDIT_ACTIONS':
        return dispatch(DISABLE_EDIT_ACTIONS())
      case 'SET_JOURNEY_COMPLETE':
        return dispatch(SET_JOURNEY_COMPLETE())
      case 'UPDATE_USER_ACTIVITY_HISTORY':
        return typeof payload === 'string'
          ? dispatch(UPDATE_USER_ACTIVITY_HISTORY(payload))
          : undefined
      case 'sendEvent':
        return dispatch(sendEvent(payload))
      default:
        return undefined
    }
  }

const authenticateDirectLine = (
  directLine: DirectLineClient,
  apiToken: string,
  user: UserObj,
): DirectLineClient => {
  const originalPostActivity = directLine.postActivity.bind(directLine)
  directLine.postActivity = (activity: Parameters<DirectLineClient['postActivity']>[0]) =>
    originalPostActivity({
      ...activity,
      channelData: {
        ...activity.channelData,
        jwt: apiToken,
        user,
      },
    })
  return directLine
}

export const registerWebchat =
  (payload: RegisterWebchatPayload) =>
  (dispatch: ThunkDispatch, getState: () => AuthRootState) => {
    const dl = payload.directLine
    const user = payload.user
    const tokenValue = dl.token

    logConversationEvent('webchat_registration', dl.conversationId, user)

    const query = new URLSearchParams(window.location.search)
    const flow = query.get('flow') ?? undefined

    dispatch(conversation(dl.conversationId))
    dispatch(token(tokenValue))
    dispatch(userId(dl.user))
    dispatch(
      userObj({
        id: user.id,
        applicationId: user.applicationId,
        customerId: user.customerId,
      }),
    )

    const { vsa } = window as WindowWithVsa
    if (vsa) {
      vsa.registerAgent(JSON.stringify(user))
    }

    const directLine = createDirectLine({ token: tokenValue })
    setWebchatBotConnection(authenticateDirectLine(directLine, payload.api.token, user))

    const localDispatch = createWebchatLocalDispatch(dispatch)
    const store = createStore(
      {},
      currentActionMiddleware(localDispatch),
      userActivityHistoryMiddleware(localDispatch),
      conversationEventsMiddleware(localDispatch, flow, getState),
    )
    setWebchatBotStore(store)
  }

export const registerActivityCallback =
  (cb: (activity: unknown) => void) => () => {
    const botConnection = getWebchatRuntime().botConnection
    if (!botConnection) {
      throw new Error('Webchat is not registered')
    }

    return botConnection.activity$.subscribe(activity => {
      if ('text' in activity && activity.text === 'Oops. Something went wrong!') {
        logErrorToDatadog('Generic activity error', {})
      }
      cb(activity)
    })
  }

export const unregisterWebchat = () => (dispatch: ThunkDispatch) => {
  dispatch(conversation(undefined))
  dispatch(token(undefined))
  dispatch(userId(undefined))
  dispatch(userObj(undefined))
  dispatch(CURRENT_ACTION(undefined))
  dispatch(CAN_EDIT_ACTIONS(false))
  dispatch(overlayReset())
  dispatch(resetForNewSession())
  clearWebchatRuntime()
}

export const selectIsWebchatRegistered = (state: { webchat: WebchatState }) => {
  const runtime = getWebchatRuntime()
  return (
    runtime.botConnection !== undefined &&
    state.webchat.conversation !== undefined &&
    runtime.store !== undefined &&
    state.webchat.token !== undefined &&
    state.webchat.userId !== undefined
  )
}

export type { WebchatState }
export default webchatSlice.reducer
